import { JSX } from 'preact';

import { useEffect, useRef } from 'preact/hooks';

import cx from 'clsx';

import { Button, Image } from '@shared/components/core';

import Cross from '@assets/icons/cross.svg';

import styles from './Overlay.module.scss';

export interface IOverlayProps {
	isOpen: boolean;
	children: JSX.Element | JSX.Element[];
	onClose?: () => void;
	showCloseButton?: boolean;
	className?: {
		overlayClassName?: string;
	};
	isFullScreenHurdle?: boolean;
	handleMobileResolution?: boolean; // This is added to avoid showing overlay as a bottom popover
}

const Overlay = ({
	isOpen,
	onClose,
	children,
	showCloseButton = true,
	className = {},
	isFullScreenHurdle = false,
	handleMobileResolution = true,
}: IOverlayProps) => {
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				onClose &&
				isOpen &&
				modalRef.current &&
				!modalRef.current.contains(event.target as Node)
			) {
				onClose();
			}
		};

		if (isOpen && onClose) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, onClose, modalRef]);

	if (!isOpen) {
		return null;
	}

	return (
		<div
			className={cx(
				'width100 height100 justify-center items-center fixed top0 left0 overflow-y-auto',
				styles.modalOverlay,
				className.overlayClassName,
				isOpen ? styles.isVisible : styles.isHidden,
			)}
		>
			<div
				className={cx(
					'bg-white border-radius3 m-auto d-flex flex-column justify-center items-center relative',
					styles.modalContent,
					{ [styles.mobileModalContent]: handleMobileResolution },
					isFullScreenHurdle && ['width100', 'height100', 'border-radius0'],
				)}
				ref={modalRef}
			>
				{showCloseButton && (
					<Button
						className={cx('absolute pointer p0', styles.closeButton)}
						onClick={onClose}
					>
						<Image src={Cross} alt="close button" />
					</Button>
				)}
				{children}
			</div>
		</div>
	);
};

export default Overlay;
